// AceptacionModal.js — Aceptación obligatoria de términos al iniciar sesión.
//
// Se configura en Admin → Proyecto → Términos (config_json.aceptacion) y está
// apagado salvo que el proyecto lo active. Al entrar, el usuario ve un modal que
// NO se puede cerrar: cada punto tiene un botón "Ver información" que abre el
// texto legal completo; al pulsar "Volver" ese punto queda marcado. Con todos
// los puntos marcados se habilita "Guardar", que registra la aceptación en la
// base de datos (tabla proyecto_aceptaciones, una fila por usuario/versión).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { aceptacionService } from '../services/api';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 4000;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.2rem;
  overflow-y: auto;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: min(820px, 100%);
  max-height: min(90vh, 900px);
  overflow-y: auto;
  padding: 2.2rem 2.4rem;
  @media (max-width: 640px) { padding: 1.4rem 1.2rem; }
`;

const Titulo = styled.h2`
  margin: 0 0 0.9rem;
  text-align: center;
  color: ${p => p.$color};
  font-size: 1.85rem;
  font-weight: 800;
  line-height: 1.2;
  @media (max-width: 640px) { font-size: 1.3rem; }
`;

const Subtitulo = styled.p`
  margin: 0 auto 1.6rem;
  text-align: center;
  color: #8a94a6;
  font-size: 1rem;
  line-height: 1.5;
  max-width: 640px;
`;

const Fila = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.45rem 0;
  @media (max-width: 640px) { flex-direction: column; align-items: flex-start; gap: 0.5rem; }
`;

const Etiqueta = styled.label`
  flex: 1;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: ${p => p.$color};
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.4;
  cursor: ${p => (p.$bloqueado ? 'not-allowed' : 'pointer')};
  opacity: ${p => (p.$bloqueado ? 0.75 : 1)};
  input {
    margin-top: 2px;
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    accent-color: ${p => p.$acento};
    cursor: inherit;
  }
`;

const BtnInfo = styled.button`
  flex-shrink: 0;
  background: transparent;
  border: 1px solid ${p => p.$acento};
  color: ${p => p.$acento};
  border-radius: 4px;
  padding: 0.5rem 0.9rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  &:hover { background: ${p => p.$acento}; color: #fff; }
  @media (max-width: 640px) { align-self: flex-end; }
`;

const BtnGuardar = styled.button`
  display: block;
  margin: 1.8rem auto 0;
  min-width: 160px;
  background: ${p => p.$color};
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 0.85rem 2rem;
  font-size: 1.05rem;
  font-weight: 700;
  cursor: pointer;
  opacity: ${p => (p.disabled ? 0.55 : 1)};
  cursor: ${p => (p.disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.15s, opacity 0.15s, filter 0.15s;
  &:hover { filter: ${p => (p.disabled ? 'none' : 'brightness(1.08)')}; }
`;

const InfoTitulo = styled.h3`
  margin: 0 0 1rem;
  color: ${p => p.$color};
  font-size: 1.4rem;
  font-weight: 800;
  text-align: center;
`;

const InfoCuerpo = styled.div`
  color: #334155;
  font-size: 0.92rem;
  line-height: 1.6;
  h2 { font-size: 1.05rem; color: ${p => p.$color}; margin: 1.2rem 0 0.5rem; }
  h3 { font-size: 1rem; margin: 1rem 0 0.4rem; }
  p { margin: 0 0 0.8rem; }
  ul { margin: 0 0 0.8rem 1.1rem; padding: 0; }
  li { margin-bottom: 0.3rem; }
  a { color: ${p => p.$acento}; }
`;

const ErrorMsg = styled.p`
  margin: 0.8rem 0 0;
  text-align: center;
  color: #dc2626;
  font-size: 0.85rem;
`;

const tl = (v, fb = '') => {
  if (v == null) return fb;
  if (typeof v === 'string') return v || fb;
  if (typeof v === 'object') return v.es || Object.values(v)[0] || fb;
  return fb;
};

const claveItem = (it, i) => it.key || `item_${i}`;
const requiereLectura = (it) => !!tl(it.info_html, '');

/**
 * Vista del modal (sin lógica de sesión): la usa tanto el modal real como la
 * vista previa del admin. `onGuardar` recibe los items aceptados.
 */
export const AceptacionVista = ({ cfg = {}, onGuardar, guardando = false, error = '' }) => {
  const items = useMemo(() => (Array.isArray(cfg.items) ? cfg.items : []), [cfg]);
  const [marcados, setMarcados] = useState({});
  const [leidos, setLeidos] = useState({});
  const [infoAbierta, setInfoAbierta] = useState(null); // índice del item

  const volverDeInfo = useCallback(() => {
    const i = infoAbierta;
    setInfoAbierta(null);
    if (i == null) return;
    const k = claveItem(items[i], i);
    setLeidos(prev => ({ ...prev, [k]: true }));
    setMarcados(prev => ({ ...prev, [k]: true }));   // al volver, queda aceptado
  }, [infoAbierta, items]);

  const todosMarcados = items.length > 0 && items.every((it, i) => marcados[claveItem(it, i)]);

  const cTitulo = cfg.color_titulo || '#0B3D91';
  const cAcento = cfg.color_acento || '#1E9CF0';
  const cBoton  = cfg.color_boton || '#7D9EC4';          // en espera (aún faltan casillas)
  const cBotonOk = cfg.color_boton_activo || '#0B3D91';  // listo para guardar
  const cItem   = cfg.color_items || '#333333';   // texto de cada casilla
  const item = infoAbierta != null ? items[infoAbierta] : null;

  return (
    <Overlay>
      <Card>
        {item ? (
          <>
            <InfoTitulo $color={cTitulo}>{tl(item.info_titulo, '') || tl(item.etiqueta, '')}</InfoTitulo>
            <InfoCuerpo
              $color={cTitulo}
              $acento={cAcento}
              dangerouslySetInnerHTML={{ __html: tl(item.info_html, '') }}
            />
            <BtnGuardar $color={cBotonOk} onClick={volverDeInfo}>
              {tl(cfg.texto_boton_volver, 'Volver')}
            </BtnGuardar>
          </>
        ) : (
          <>
            <Titulo $color={cTitulo}>
              {tl(cfg.titulo, 'Aceptación de términos, condiciones y uso de la información')}
            </Titulo>
            {tl(cfg.subtitulo, '') && <Subtitulo>{tl(cfg.subtitulo, '')}</Subtitulo>}

            {items.map((it, i) => {
              const k = claveItem(it, i);
              const bloqueado = requiereLectura(it) && !leidos[k];
              return (
                <Fila key={k}>
                  <Etiqueta $color={cItem} $acento={cAcento} $bloqueado={bloqueado}>
                    <input
                      type="checkbox"
                      checked={!!marcados[k]}
                      disabled={bloqueado}
                      onChange={e => setMarcados(prev => ({ ...prev, [k]: e.target.checked }))}
                    />
                    <span>{tl(it.etiqueta, k)}</span>
                  </Etiqueta>
                  {requiereLectura(it) && (
                    <BtnInfo type="button" $acento={cAcento} onClick={() => setInfoAbierta(i)}>
                      {tl(cfg.texto_boton_info, 'Ver información')}
                    </BtnInfo>
                  )}
                </Fila>
              );
            })}

            <BtnGuardar
              $color={todosMarcados ? cBotonOk : cBoton}
              disabled={!todosMarcados || guardando}
              onClick={() => todosMarcados && !guardando && onGuardar && onGuardar(
                items.map((it, i) => ({ key: claveItem(it, i), etiqueta: tl(it.etiqueta, '') }))
              )}
            >
              {guardando ? 'Guardando…' : tl(cfg.texto_boton, 'Guardar')}
            </BtnGuardar>
            {error && <ErrorMsg>{error}</ErrorMsg>}
          </>
        )}
      </Card>
    </Overlay>
  );
};

const AceptacionModal = () => {
  const { proyecto } = useProject();
  const { user } = useAuth();

  const cfg = useMemo(() => proyecto?.config_json?.aceptacion || {}, [proyecto]);
  const items = useMemo(() => (Array.isArray(cfg.items) ? cfg.items : []), [cfg]);
  const version = Number(cfg.version || 1);

  const [visible, setVisible] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const activo = cfg.activo === true && items.length > 0 && !!proyecto?.id;
  // El admin gestiona la plataforma: no se le bloquea (lo prueba desde el
  // editor con "Vista previa").
  const aplicaAlUsuario = !!user?.id && user.role !== 'admin';

  useEffect(() => {
    let vivo = true;
    if (!activo || !aplicaAlUsuario) { setVisible(false); return undefined; }
    aceptacionService.estado(proyecto.id, version)
      .then(r => { if (vivo) setVisible(!r.data?.aceptado); })
      .catch(() => { /* si falla la consulta no bloqueamos el acceso */ });
    return () => { vivo = false; };
  }, [activo, aplicaAlUsuario, proyecto, version, user]);

  // Modal bloqueante: sin scroll del fondo mientras está abierto.
  useEffect(() => {
    if (!visible) return undefined;
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previo; };
  }, [visible]);

  const guardar = async (itemsAceptados) => {
    setGuardando(true);
    setError('');
    try {
      await aceptacionService.aceptar({ proyecto_id: proyecto.id, version, items: itemsAceptados });
      setVisible(false);
    } catch (e) {
      setError('No se pudo guardar tu aceptación. Inténtalo de nuevo.');
    }
    setGuardando(false);
  };

  if (!visible) return null;

  return <AceptacionVista cfg={cfg} onGuardar={guardar} guardando={guardando} error={error} />;
};

export default AceptacionModal;
